import autoTable from 'jspdf-autotable';
import {
  AGE_TYPES,
  COLOR,
  RELATIONSHIP,
  RELATIONSHIP_TO_MINOR,
  RELATIONSHIP_WITH_PERMIT,
} from '../../config/constants';
import { createPdfDocument, subtitleSize, addText } from '../pdf';
import { orderBy } from '../array';

const MAX_NAME_LENGTH = 15;
const MAX_POSITION_LENGTH = 20;

const splitTextIfNeeded = (text, maxTextLength) => {
  if (text.length <= maxTextLength) {
    return { firstPart: text, secondPart: '' };
  }

  if (text[maxTextLength] !== ' ' && text[maxTextLength - 1] !== ' ') {
    let start = maxTextLength;
    let end = maxTextLength;

    while (start > 0 && text[start - 1] !== ' ') {
      start -= 1;
    }

    while (end < text.length && text[end] !== ' ') {
      end += 1;
    }

    const firstPart = text.slice(0, end).trim();
    const secondPart = text.slice(end).trim();

    return { firstPart, secondPart };
  }

  let firstPart = '';
  let secondPart = text;

  for (let i = maxTextLength; i > 0; i -= 1) {
    if (text[i] === ' ') {
      firstPart = text.slice(0, i).trim();
      secondPart = text.slice(i).trim();
      break;
    }
  }

  if (!firstPart) {
    firstPart = text.slice(0, maxTextLength).trim();
    secondPart = text.slice(maxTextLength).trim();
  }

  return { firstPart, secondPart };
};

const printDocumentHeader = (doc, manifest, secondManifest = false) => {
  const x = 5;
  let y = 5;
  addText(doc, 'TURISMO CIVA S.A.C. 20102427891', x + 70, y);
  addText(doc, 'Jr. Sancho de Rivera 1184 - Cercado de Lima', x + 60, (y += 5));
  // addText(doc, '20102427891', x, (y += 5));

  if (secondManifest) {
    addText(
      doc,
      `MANIFIESTO DE USUARIOS N° ${manifest.manifestNumber}`,
      doc.internal.pageSize.getWidth() - 100,
      (y += 8),
    );
  } else {
    addText(
      doc,
      `MANIFIESTO DE PASAJEROS N° ${manifest.manifestNumber}`,
      doc.internal.pageSize.getWidth() - 100,
      (y += 8),
    );
  }

  if (!manifest.hasSalesSessionUser) {
    addText(doc, 'N° Resolución', x, y);
    addText(doc, manifest.resolutionNumber.toString(), x + 40, y);
    addText(doc, 'Autorización Sunat', x, (y += 5));
    addText(doc, manifest.taxAuthorizationNumber.toString(), x + 40, y);
  }
};

const printRouteHeader = (doc, manifest) => {
  let x = 5;
  let y = 56;

  doc.rect(x - 2, y - 4, 130, 5);

  addText(doc, 'RUTA', 65, y);

  addText(doc, 'Origen', x, (y += 5));
  addText(doc, manifest.origin.substring(0, 22), x + 16, y);

  addText(doc, 'Fecha', x, (y += 5));
  addText(doc, manifest.tripDate, x + 16, y);

  addText(doc, 'Hora', x + 50, y);
  addText(doc, manifest.tripTime, x + 62, y);

  addText(doc, 'Destino', (x + 85), (y -=5));
  addText(doc, manifest.destination, x + 103, y);

  addText(doc, 'Servicio', x + 85, (y += 5));
  addText(doc, manifest.bus.seatMap.displayName, x + 103, y);

  addText(doc, 'Dirección Partida', x, (y += 5));
  addText(doc, manifest.startAddress, x + 35, y);
  const formatBySegments = (text) => {
  if (!text) return '-';
  const cleanText = text.replace(/\n/g, '').replace(/\r/g, '');
  const segments = cleanText.split(';').filter((s) => s.trim());
  if (!segments.length) return '-';
  return segments.map((segment) => `-${segment.trim()}`).join(' ');
};

  addText(doc, 'Embarque', x, (y += 5));
  const maxWidth = doc.internal.pageSize.getWidth() - (x + 35) - 10;
  const boardingLines = doc.splitTextToSize(formatBySegments(manifest.boarding), maxWidth);
  boardingLines.forEach((line, index) => {
    addText(doc, line, x + 35, y + (index * 4));
  });
  if (boardingLines.length > 1) {
    y += (boardingLines.length - 1) * 4;
  }

  addText(doc, 'Desembarque', x, (y += 6));
  const disembarkingLines = doc.splitTextToSize(formatBySegments(manifest.disembarking), maxWidth);
  disembarkingLines.forEach((line, index) => {
    addText(doc, line, x + 35, y +(index * 4));
  });
  if (disembarkingLines.length > 1) {
    y += (disembarkingLines.length - 1) * 4;
  }
  
};

const printBusHeader = (
  doc,
  {
    bus,
    circulationPermissionNumber,
    circulationPermissionExpDate = '',
    hasSalesSessionUser,
  },
) => {
  let x = 5;
  let y = 30;

  doc.rect(x - 2, y - 4, 130, 5);

  addText(doc, 'BUS', 66, y);

  addText(doc, 'Código Bus', x, (y += 5));
  addText(doc, bus.companyBusId.toString(), x + 25, y);

  addText(doc, 'Placa', x, (y += 5));
  addText(doc, bus.licensePlate, x + 25, y);

  addText(doc, 'Marca', x, (y += 5));
  addText(doc, bus.busChassis.make, x + 25, y);

  addText(doc, 'Modelo', x, (y += 5));
  addText(doc, bus.busChassis.model, x + 25, y);

  if (!hasSalesSessionUser) {
    addText(doc, 'Certificado\nHabilitacion', (x += 75), (y -= 15));
    addText(doc, circulationPermissionNumber || '', x + 22, (y += 3));

    addText(doc, 'Vencimiento\nCertificado', x, (y += 6));
    addText(doc, circulationPermissionExpDate || '', x + 22, (y += 3));
  }
};

const printCapacityHeader = (doc, manifest, secondManifest = false) => {
  const x = 147;
  let y = 30;

  doc.rect(x - 2, y - 4, 30, 5);

  addText(doc, 'CAPACIDAD', x + 3, y);

  addText(doc, 'Cap. máx. asientos', x, (y += 5));
  addText(doc, manifest.busCapacity.toString(), x + 42, y);

  if (!manifest.hasSalesSessionUser) {
    if (!secondManifest) {
      addText(doc, 'Asientos ocup. trip.', x, (y += 5));
      addText(doc, manifest.numStaffOnBoard.toString(), x + 42, y);

      addText(doc, 'Asientos ocup. pax.', x, (y += 5));
      addText(
        doc,
        (
          Number(manifest.busCapacity) - Number(manifest.numStaffOnBoard)
        ).toString(),
        x + 42,
        y,
      );
    }

    addText(doc, 'N° pasajeros', x, (y += 5));
    addText(doc, manifest.numPassengers.toString(), x + 42, y);
  }
};

const printCrewHeader = (doc, manifest) => {
  const x = 5;
  let y = 100;

  addText(doc, 'TRIPULACIÓN', x + 5, y);
  y += 5;

  if (manifest.drivers) {
    manifest.drivers.forEach((driver) => {
      addText(doc, 'Conductor', x, y);
      addText(doc, driver.fullName, x + 30, y);
      addText(doc, 'N° Licencia', x + 125, y);
      addText(
        doc,
        driver.licenseNumber ? driver.licenseNumber.toString() : '',
        x + 150,
        y,
      );
      y += 5;
    });
  }

  if (manifest.cabinCrew) {
    manifest.cabinCrew.forEach((cabinCrew) => {
      addText(doc, 'Tripulante SAB', x, y);
      addText(doc, cabinCrew.cabinCrew.customer.fullName, x + 30, y);
      y += 5;
    });
  }
  return { xpos: x, ypos: y };
};

const printPassengers = (
  doc,
  manifest,
  { xpos, ypos },
  isSecondManifest = false,
  isPassengerManifestCopy = false,
) => {
  const x = xpos;
  let y = ypos;

  const head = !manifest.hasSalesSessionUser
    ? [
        [
          {
            content: 'ITEM',
            styles: { halign: 'right' },
          },
          'NOMBRE PASAJERO',
          'DOCUMENTO',
          'N° BOLETO',
          { content: 'ASIENTO', styles: { halign: 'center' } },
          'EDAD',
          isSecondManifest
            ? 'ORIGEN - DESTINO'
            : { content: 'COSTO', styles: { halign: 'right' } },
          'OBS.',
        ],
      ]
    : [
        [
          {
            content: 'Nro.',
            styles: { halign: 'right' },
          },
          'Apellido y Nombres',
          'DNI',
          'Empresa',
          'Puesto',
          { content: 'Ato.', styles: { halign: 'center' } },
          'Origen',
          'Destino',
        ],
      ];

  if (manifest.floors) {
    let rowNumber = 0;
    const fullPassengerList = manifest.floors.reduce(
      (acc, floor) => [...acc, ...floor.manifestPassengerList],
      [],
    );
    manifest.floors.forEach((floor) => {
      y = doc.autoTable.previous
        ? Math.round(doc.autoTable.previous.finalY) + 5
        : y + 5;
      addText(doc, `Piso: ${floor.floorNumber}`, x, y);

      const passengerList = floor.manifestPassengerList.map((row) => ({
        ...row,
        seatNumber: +row.seatNumber,
      }));
      const passengerListSorted = !manifest.hasSalesSessionUser
        ? orderBy(passengerList, ['seatNumber'])
        : orderBy(passengerList, ['fullName']);

      const body = [];
      if (!manifest.hasSalesSessionUser) {
        passengerListSorted.forEach(
          ({
            ageType,
            age,
            fullName,
            documentType,
            idDocumentNumber,
            ticketNumber,
            seatNumber,
            salePrice,
            destinationName,
            relationship,
            notarizedPermit,
            boardingIATACode,
            disembarkingIATACode,
            useRealId,
            id,
          }) => {
            rowNumber += 1;
            const [idInteger, idDecimal] = id.split('.');
            const myAgeType = ageType ? ` ${AGE_TYPES[ageType].label}` : '';
            const myAge = age && age !== 'null' ? ` ${age}${myAgeType}` : '';
            let completeComments = '';

            const buildTravelWithComment = (previousPassenger) => {
              if (!previousPassenger || !relationship) return null;
              const needNotarizedPermit = RELATIONSHIP_WITH_PERMIT[relationship] !== undefined;
              const relationshipLabel = `Viaja con "${RELATIONSHIP[relationship].label}"`;
              const seatLabel = `Asiento ${previousPassenger.seatNumber}`;
              const ref = `Ref. ${previousPassenger.ticketNumber}`;
              const np = needNotarizedPermit ? `\nPN: ${notarizedPermit || ''}` : '';

              return `${relationshipLabel}\n${seatLabel}\n${ref}${np}`;
            };

            if (
              relationship &&
              RELATIONSHIP_TO_MINOR[relationship] ===
                RELATIONSHIP_TO_MINOR.TRAVELS_ALONE
            ) {
              const needNotarizedPermit = RELATIONSHIP_WITH_PERMIT[relationship] !== undefined;
              const np = needNotarizedPermit ? `\nPN: ${notarizedPermit || ''}` : '';
              completeComments = `Viaja Solo${np}`;
            } else if (idDecimal > 5) {
              // kids with ticket
              const previousPassenger = fullPassengerList.find(
                (passenger) => passenger.id === `${idInteger}.0`,
              );

              const comment = buildTravelWithComment(previousPassenger);
              if (comment) completeComments = comment;
            } else if (+idDecimal > 0) {
              // kids without ticket
              const previousPassenger = useRealId ? fullPassengerList.find(
                (passenger) => passenger.realId === `${idInteger}.0`,
              ) : fullPassengerList.find(
                (passenger) => passenger.id === `${idInteger}.0`,
              );

              const comment = buildTravelWithComment(previousPassenger);
              if (comment) completeComments = comment;
            }

            body.push([
              {
                content: rowNumber,
                styles: {
                  halign: 'right',
                },
              },
              fullName,
              `${documentType} ${idDocumentNumber}`,
              ticketNumber,
              {
                content: seatNumber,
                styles: { halign: 'center' },
              },
              myAge,
              !isSecondManifest
                ? {
                    content: salePrice.toFixed(2),
                    styles: {
                      halign: 'right',
                    },
                  }
                : `${boardingIATACode}-${disembarkingIATACode}`,
              {
                content: completeComments,
                styles: {
                  halign: 'left',
                },
              },
            ]);
          },
        );
      } else {
        passengerListSorted.forEach(
          ({
            fullName,
            idDocumentNumber,
            seatNumber,
            businessName,
            position,
            originName,
            destinationName,
          }) => {
            rowNumber += 1;

            let nameTop = fullName;
            let nameBottom = '';

            if (manifest.hasSalesSessionUser) {
              const { firstPart, secondPart } = splitTextIfNeeded(
                fullName,
                MAX_NAME_LENGTH,
              );
              nameTop = firstPart;
              nameBottom = secondPart;
            }

            let positionTop = position;
            let positionBottom = '';

            if (manifest.hasSalesSessionUser) {
              const { firstPart, secondPart } = splitTextIfNeeded(
                position,
                MAX_POSITION_LENGTH,
              );
              positionTop = firstPart;
              positionBottom = secondPart;
            }

            body.push([
              {
                content: rowNumber,
                styles: {
                  halign: 'right',
                },
              },
              nameTop,
              idDocumentNumber,
              businessName,
              positionTop,
              {
                content: seatNumber,
                styles: { halign: 'center' },
              },
              originName,
              destinationName,
            ]);

            if (nameBottom || positionBottom) {
              body.push([
                { content: '' },
                nameBottom,
                { content: '', colSpan: 2 },
                positionBottom,
                { content: '', colSpan: 3 },
              ]);
            }
          },
        );
      }

      autoTable(doc, {
        head,
        body,
        headStyles: {
          fillColor: COLOR.white,
          lineColor: '#000',
          lineWidth: { top: 0.1, bottom: 0.1 },
        },
        startY: (y += 1),
        showHead: 'everyPage',
        margin: 5,
        theme: 'striped',
        styles: {
          overflow: 'linebreak',
          cellPadding: 0.5,
          textColor: 0,
          fontSize: !manifest.hasSalesSessionUser ? 10 : 8,
        },
        columnStyles: !manifest.hasSalesSessionUser
          ? {
              0: { cellWidth: 10 }, // ITEM
              1: { cellWidth: 35 }, // NOMBRE PASAJERO
              2: { cellWidth: 25 }, // DOCUMENTO
              3: { cellWidth: 25 }, // N° BOLETO
              4: { cellWidth: 17 }, // ASIENTO
              5: { cellWidth: 18 }, // EDAD
              6: isSecondManifest ? { cellWidth: 25 } : { cellWidth: 20 }, // RUTA
              7: isSecondManifest ? { cellWidth: 45 } : { cellWidth: 50 }, // OBS.
            }
          : undefined,
      });
    });
    y = doc.autoTable.previous
      ? Math.round(doc.autoTable.previous.finalY) + 5
      : y + 5;
    if (!manifest.hasSalesSessionUser && !isSecondManifest) {
      if (isPassengerManifestCopy) {
        addText(doc, 'TRANSPORTISTA', doc.internal.pageSize.getWidth() - 36, y);
      } else {
        addText(doc, 'SUNAT', doc.internal.pageSize.getWidth() - 18, y);
      }
    }
  }
};

const printManifest = (manifest) => {
  const doc = createPdfDocument();

  doc.autoTable.previous = null;

  doc.setFontSize(subtitleSize);
  doc.setTextColor(COLOR.black);

  printDocumentHeader(doc, manifest);
  printBusHeader(doc, manifest);
  printRouteHeader(doc, manifest);
  printCapacityHeader(doc, manifest);
  const position = printCrewHeader(doc, manifest, 5, 110);
  printPassengers(doc, manifest, position);

  // copy manifest passengers

  doc.addPage();
  doc.autoTable.previous = null;
  doc.setFontSize(subtitleSize);
  doc.setTextColor(COLOR.black);
  printDocumentHeader(doc, manifest);
  printBusHeader(doc, manifest);
  printRouteHeader(doc, manifest);
  printCapacityHeader(doc, manifest);
  const position1 = printCrewHeader(doc, manifest, 5, 110);
  printPassengers(doc, manifest, position1, false, true);

  // print user manifest

  doc.addPage();
  doc.autoTable.previous = null;
  doc.setFontSize(subtitleSize);
  doc.setTextColor(COLOR.black);
  printDocumentHeader(doc, manifest, true);
  printBusHeader(doc, manifest);
  printRouteHeader(doc, manifest);
  printCapacityHeader(doc, manifest, true);
  const position2 = printCrewHeader(doc, manifest, 5, 110);
  printPassengers(doc, manifest, position2, true);

  window.open(doc.output('bloburl'), '_blank');
};

// eslint-disable-next-line import/prefer-default-export
export { printManifest };
